import { useState } from 'react';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Island, Page, ServiceType, VisitingOfficerSchedule } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

const ISLANDS: Island[] = ['MAHE', 'PRASLIN', 'LA_DIGUE', 'SILHOUETTE', 'OTHER'];

export function VisitingOfficers() {
  const { persona } = useAuth();
  const canSchedule = persona?.roles.includes('SCHEDULER') ?? false;
  const [island, setIsland] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const listQ = useApiQuery<Page<VisitingOfficerSchedule>>(
    `/api/v1/servicedesk/visiting-officer-schedules?limit=100${island ? `&island=${island}` : ''}`,
  );

  const cancel = async (s: VisitingOfficerSchedule) => {
    setActionError(null);
    try {
      await api.post(`/api/v1/servicedesk/visiting-officer-schedules/${s.id}/cancel`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Cancel failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Visiting Officer Schedules"
        subtitle="Outer-island circuits for offices without permanent counter staff."
        actions={
          <div className="flex items-center gap-2">
            <select className="field-input !w-auto" value={island} onChange={(e) => setIsland(e.target.value)}>
              <option value="">All islands</option>
              {ISLANDS.map((i) => (
                <option key={i} value={i}>
                  {i.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
            {canSchedule ? (
              <button className="btn-primary" onClick={() => setShowCreate(true)}>
                + New schedule
              </button>
            ) : null}
          </div>
        }
      />

      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No schedules yet" hint="Circuit officers rotate through islands without daily counter staff." />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Officer</th>
                <th className="table-head">Island</th>
                <th className="table-head">Visit dates</th>
                <th className="table-head">Services</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((s) => (
                <tr key={s.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{s.officerId}</td>
                  <td className="table-cell">
                    <StatusBadge status={s.island} />
                  </td>
                  <td className="table-cell text-xs">{s.dates.map((d) => formatDate(d)).join(', ')}</td>
                  <td className="table-cell text-xs text-slate-500">{s.services.join(', ')}</td>
                  <td className="table-cell text-right">
                    {canSchedule ? (
                      <button className="btn-danger !px-2.5 !py-1 text-xs" onClick={() => cancel(s)}>
                        Cancel
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const serviceTypesQ = useApiQuery<Page<ServiceType>>('/api/v1/servicedesk/service-types?limit=100');
  const [officerId, setOfficerId] = useState('');
  const [island, setIsland] = useState<Island>('SILHOUETTE');
  const [dateInput, setDateInput] = useState('');
  const [dates, setDates] = useState<string[]>([]);
  const [services, setServices] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const addDate = () => {
    if (!dateInput || dates.includes(dateInput)) return;
    setDates((d) => [...d, dateInput].sort());
    setDateInput('');
  };

  const toggleService = (code: string) => {
    setServices((s) => (s.includes(code) ? s.filter((c) => c !== code) : [...s, code]));
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/servicedesk/visiting-officer-schedules', {
        officerId,
        island,
        dates,
        services,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to create schedule');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New visiting-officer schedule" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Officer ID (SeyID subject)">
            <input className="field-input" value={officerId} onChange={(e) => setOfficerId(e.target.value)} placeholder="e.g. acc_bernard_esparon" />
          </Field>
          <Field label="Island">
            <select className="field-input" value={island} onChange={(e) => setIsland(e.target.value as Island)}>
              {ISLANDS.map((i) => (
                <option key={i} value={i}>
                  {i.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <Field label="Visit dates">
          <div className="flex gap-2">
            <input type="date" className="field-input" value={dateInput} onChange={(e) => setDateInput(e.target.value)} />
            <button className="btn-secondary shrink-0" onClick={addDate} disabled={!dateInput}>
              Add date
            </button>
          </div>
          {dates.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {dates.map((d) => (
                <span key={d} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                  {formatDate(d)}
                  <button className="text-slate-400 hover:text-slate-700" onClick={() => setDates((ds) => ds.filter((x) => x !== d))}>
                    ✕
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </Field>

        <Field label="Services covered">
          {serviceTypesQ.loading ? (
            <Spinner />
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {(serviceTypesQ.data?.items ?? []).map((st) => (
                <label
                  key={st.code}
                  className={`flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition ${
                    services.includes(st.code) ? 'border-brand-500 bg-brand-50 text-brand-700' : 'border-slate-300 text-slate-600'
                  }`}
                >
                  <input type="checkbox" className="hidden" checked={services.includes(st.code)} onChange={() => toggleService(st.code)} />
                  {st.code}
                </label>
              ))}
            </div>
          )}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={submit}
            disabled={busy || !officerId.trim() || dates.length === 0 || services.length === 0}
          >
            {busy ? 'Scheduling…' : 'Create schedule'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
